// ========================================
// MySQL 기반 세션 관리 유틸리티 함수
// - 세션 토큰은 sessionStorage에 저장 (브라우저 탭 닫으면 삭제됨)
// - RN(WebView) 환경에서는 postMessage로 세션 정보를 네이티브에 전달
// ========================================

const SESSION_TOKEN_KEY = 'hb_session_token';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

// RN(WebView) 환경으로 메시지 전송
const postToNative = (payload) => {
    try {
        if (typeof window !== "undefined" && window.ReactNativeWebView && payload) {
            window.ReactNativeWebView.postMessage(JSON.stringify(payload));
        }
    } catch (e) {
        console.error("RN postMessage 오류:", e);
    }
};

/**
 * 세션 생성 (로그인 성공 시 호출)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<string|null>} sessionToken 또는 null
 */
export const createSession = async (userId) => {
    try {
        const res = await fetch(`${API_BASE_URL}/auth/create-session`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId }),
        });

        if (res.ok) {
            const data = await res.json();
            if (data.success && data.sessionToken) {
                sessionStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
                console.log('✅ 세션 생성 완료 (sessionStorage에 저장)');

                // RN(WebView)에 세션 생성 알림
                postToNative({
                    type: "SESSION_CREATED",
                    sessionToken: data.sessionToken,
                    userId,
                });
                return data.sessionToken;
            }
        }
        console.error('세션 생성 실패:', res.status);
        return null;
    } catch (e) {
        console.error('세션 생성 중 오류:', e);
        return null;
    }
};

/**
 * 세션 확인 (MySQL에서 확인)
 * @returns {Promise<{isValid: boolean, status?: string}>} 세션 정보 반환
 */
export const checkSession = async () => {
    const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    const email = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
    
    if (!sessionToken && !email) {
        return { isValid: false };
    }

    try {
        const url = sessionToken
            ? `${API_BASE_URL}/auth/check-session?sessionToken=${encodeURIComponent(sessionToken)}`
            : `${API_BASE_URL}/auth/check-session?email=${encodeURIComponent(email)}`;

        const res = await fetch(url, {
            method: "GET",
        });

        if (res.ok) {
            const data = await res.json();
            if (data.isValid && data.sessionToken) {
                // 서버에서 새로운 sessionToken을 반환한 경우 업데이트
                sessionStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);

                // RN(WebView)에 세션 갱신 알림
                postToNative({
                    type: "SESSION_UPDATED",
                    sessionToken: data.sessionToken,
                });
            }
            return {
                isValid: data.isValid || false,
                status: data.status || null
            };
        }
        return { isValid: false };
    } catch (e) {
        console.error('세션 확인 중 오류:', e);
        return { isValid: false };
    }
};

/**
 * 모든 localStorage 및 sessionStorage 항목 삭제
 * 로그아웃 시 또는 계정 전환 시 호출
 */
export const clearAllStorage = () => {
    // sessionStorage 항목 삭제
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    
    // localStorage 항목 모두 삭제
    const localStorageKeys = [
        // 세션/인증 관련
        'hb_session_token',
        'hb_backendToken',
        'hb_backendUserId',
        'hb_otp',
        'hb_isLoggedIn',
        
        // 사용자 정보 관련
        'hb_serverName',
        'hb_googleName',
        'hb_serverEmail',
        'hb_googleEmail',
        'hb_googlePicture',
        'hb_userLevel',
        'hb_birthday',
        'hb_userGrade',
        
        // Google OAuth 관련
        'hb_googleSub',
        'hb_googleAccessToken',
        'hb_hasGoogleAccount',
    ];
    
    localStorageKeys.forEach(key => {
        localStorage.removeItem(key);
    });
    
    console.log('🗑️ 모든 스토리지 항목 삭제 완료');
};

/**
 * 세션 삭제 (로그아웃 시 호출)
 */
export const deleteSession = async () => {
    const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    const email = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
    
    try {
        // 세션 토큰이나 이메일이 있으면 서버에 세션 삭제 요청
        if (sessionToken || email) {
            await fetch(`${API_BASE_URL}/auth/delete-session`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ sessionToken, email }),
            });
        }
    } catch (e) {
        console.error('세션 삭제 중 오류:', e);
    } finally {
        // 모든 스토리지 항목 삭제
        clearAllStorage();
        
        // RN(WebView)에 로그아웃 알림 (모든 정보 삭제)
        postToNative({
            type: "LOGOUT",
            clearAll: true,
        });
    }
};

/**
 * 세션 스토리지에서 세션 토큰 가져오기
 * @returns {string|null}
 */
export const getSessionToken = () => {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
};

/**
 * (RN용) 외부에서 세션 토큰을 주입할 때 사용
 *  - RN에서 저장하고 있던 sessionToken을 WebView로 넘겨서 재사용할 수 있음
 */
export const setSessionTokenFromNative = (token) => {
    if (!token) return;
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    postToNative({
        type: "SESSION_SYNCED",
        sessionToken: token,
    });
};

// 전역(window)에 노출하여 RN WebView에서 직접 호출할 수 있게 함
if (typeof window !== "undefined") {
    // eslint-disable-next-line no-undef
    window.setSessionTokenFromNative = setSessionTokenFromNative;
}